import { z } from 'zod';
import { COACH_ACTIONS, COACH_ACTION_IDS, type CoachActionId } from './actions';
import {
  COACH_ANCHOR_IDS,
  COACH_ANCHORS,
  type CoachAnchorDefinition,
  type CoachAnchorId,
} from './anchors';
import { COACH_EVENTS, COACH_EVENT_NAMES, type CoachEventName } from './events';
import { COACH_CONTENT_TYPES, COACH_FORMS, COACH_FORM_IDS, type CoachFormId } from './forms';
import { COACH_SLOT_IDS, type CoachSlotId } from './slots';

/**
 * Who a journey is for: a feature flag, or a fact about the person. The app maps each one to
 * its real check (lib/coach/useCoachGates.ts, typed against this list).
 */
export const COACH_GATES = {
  scriptShootPost: 'Script Shoot Post',
  wbbcCustomer: "Bought through Simon Squibb's World's Biggest Business Competition",
} as const;

export type CoachGate = keyof typeof COACH_GATES;

/** Reserved step id: moving here ends the journey as completed. */
export const END_STEP = 'end';

const ContextKeySchema = z.string().regex(/^[a-z][a-zA-Z0-9]*$/, 'camelCase context key');
const IdSchema = z.string().regex(/^[a-z0-9-]+$/, 'lowercase id with hyphens');

/** The logos a choice button can carry; the app draws each one. */
export const COACH_CHOICE_ICONS = [
  'tiktok',
  'instagram',
  'youtube',
  'youtube-shorts',
  'meta',
  'email',
  'copy',
  'vsl',
] as const;
export type CoachChoiceIcon = (typeof COACH_CHOICE_ICONS)[number];
const ChoiceIconSchema = z.enum(COACH_CHOICE_ICONS);
const AnchorIdSchema = z.enum(COACH_ANCHOR_IDS as [CoachAnchorId, ...CoachAnchorId[]]);
const EventNameSchema = z.enum(COACH_EVENT_NAMES as [CoachEventName, ...CoachEventName[]]);
const SlotIdSchema = z.enum(COACH_SLOT_IDS as [CoachSlotId, ...CoachSlotId[]]);
const ActionIdSchema = z.enum(COACH_ACTION_IDS as [CoachActionId, ...CoachActionId[]]);
const FormIdSchema = z.enum(COACH_FORM_IDS as [CoachFormId, ...CoachFormId[]]);
const ContextValueSchema = z.union([z.string(), z.boolean()]);

/**
 * Facts the app reports about the person and the screen, so a step can read (or happen)
 * differently: a presentation's `when` picks how a step shows, a step's `when` whether it
 * happens at all.
 */
export const CoachConditionSchema = z
  .object({
    /** Whether the studio of the journey's Content Project (`projectNodeId`) is open. */
    studio: z.enum(['open', 'closed']).optional(),
    /** Whether the person has a brand (the voice Poppy writes in) to choose. */
    brand: z.enum(['chosen', 'none']).optional(),
  })
  .strict();

const SideSchema = z.enum(['top', 'right', 'bottom', 'left']);

/** A picture or short video on the card: a file in the app's public folder, or a link. */
export const CoachMediaSchema = z
  .object({
    kind: z.enum(['image', 'video']),
    src: z.string().regex(/^(\/|https:\/\/)/, 'a /public path or an https link'),
    alt: z.string().min(1),
  })
  .strict();

/**
 * One way to show a step. A step lists them in priority order; the first whose
 * `when` holds and whose anchor is on screen wins. A presentation without an
 * anchor always qualifies and shows as a floating card. Repeating anchors are
 * narrowed by the journey context: by the key named in `in`, or else the anchor's own
 * (`chat.input` → `chatNodeId`); with neither bound yet, any instance on screen.
 */
export const CoachPresentationSchema = z
  .object({
    anchor: AnchorIdSchema.optional(),
    /** For a repeating anchor: the context key holding which one (default: the anchor's own). */
    in: ContextKeySchema.optional(),
    /** A node on the board the journey keeps in context (a group it copied, a carousel). */
    node: ContextKeySchema.optional(),
    when: CoachConditionSchema.optional(),
    title: z.string().min(1).optional(),
    body: z.string().min(1).optional(),
    media: CoachMediaSchema.optional(),
    /** The side the card sits on; without one, the side with room. */
    placement: SideSchema.optional(),
    /** Whether the rest of the screen dims around what the card points at. */
    spotlight: z.boolean().default(true),
  })
  .strict()
  .refine(presentation => !(presentation.anchor && presentation.node), {
    message: 'point at an anchor or a node, not both',
  })
  .refine(
    presentation =>
      !presentation.in ||
      Boolean(
        presentation.anchor && (COACH_ANCHORS[presentation.anchor] as CoachAnchorDefinition).scope
      ),
    { message: '`in` narrows a repeating anchor; this one does not repeat', path: ['in'] }
  );

export const CoachTransitionSchema = z
  .object({
    event: EventNameSchema,
    /** Payload fields that must equal the journey context (skipped while the context key is unset). */
    match: z.array(ContextKeySchema).default([]),
    /**
     * Payload fields that must equal a differently named context key, which must be set:
     * `{ nodeId: 'creatorNodeId' }` hears `node.ready` for that one node only.
     */
    matchAs: z.record(z.string(), ContextKeySchema).optional(),
    /** Payload fields copied into the journey context. */
    bind: z.array(ContextKeySchema).default([]),
    /** Payload fields kept under another context key: `{ nodeId: 'aboutNodeId' }`. */
    bindAs: z.record(z.string(), ContextKeySchema).optional(),
    /** Ignore events about things that existed before this step began. */
    fresh: z.boolean().optional(),
    /** Payload fields that must have this value, or one of these values. */
    if: z.record(z.string(), z.union([ContextValueSchema, z.array(z.string()).min(1)])).optional(),
    to: IdSchema,
  })
  .strict();

/**
 * Work a step does through the app (actions.ts): when the step begins, or for a step with a
 * form, once the person answers. A step can list several, done in order (a setup step that
 * lays out a whole board); each one's `bind` is in context for the ones after it. It runs once
 * per visit to the step; a reload during it does not run it again (give the step a `resumeAt`
 * to choose where a reload lands).
 */
export const CoachWorkSchema = z
  .object({
    action: ActionIdSchema,
    /** Checked against the action's own params when the journey loads. */
    params: z.record(z.string(), z.unknown()).default({}),
    /** What the action returns, kept in context: `{ chatNodeId: 'chatNodeId' }`. */
    bind: z.record(z.string(), ContextKeySchema).default({}),
    /**
     * Where to go once it (and, in a list, everything before it) worked; only the last in a
     * list takes one. Without one the step stays and waits for its events.
     */
    then: IdSchema.optional(),
    /** Where to go if this one failed. Without one the card shows Try again. */
    onError: IdSchema.optional(),
  })
  .strict();

const ContentTypeSchema = z.enum(COACH_CONTENT_TYPES);

/**
 * What the card asks for. The answer is kept in context under `bind`: the words typed, for a
 * field (`text`, `link`, `creator-profile`); the new node's id, for `content`, which offers
 * the board's own ways to add something (upload, record, write, paste a link) and takes the
 * first thing of an `accept`ed type the person adds while the card is up.
 */
export const CoachFormSchema = z
  .object({
    id: FormIdSchema,
    bind: ContextKeySchema,
    /** The button that sends a typed answer. */
    submit: z.string().min(1).optional(),
    placeholder: z.string().min(1).optional(),
    /**
     * For a typed answer: ready answers the person can tap to fill the box (and then change),
     * so a big question never starts from a blank page.
     */
    suggestions: z
      .array(z.object({ label: z.string().min(1).max(28), text: z.string().min(1) }).strict())
      .min(2)
      .max(8)
      .optional(),
    /** For `content`: the node types that count as an answer, offered first in this order. */
    accept: z.array(ContentTypeSchema).min(1).optional(),
    /** Where to go once answered, for a step with no work to do with the answer. */
    to: IdSchema.optional(),
  })
  .strict();

export const CoachStepSchema = z
  .object({
    id: IdSchema,
    /** Progress shown as "chapter of total"; steps without one show no progress. */
    chapter: z.number().int().positive().optional(),
    /**
     * `card` points at an anchor (or floats), `toast` is a quiet status line,
     * `inline` shows inside a product slot, `hidden` waits silently (e.g. until
     * the user opens a board).
     */
    display: z.enum(['card', 'toast', 'inline', 'hidden', 'conversation']).default('card'),
    /** Where an `inline` step shows. */
    slot: SlotIdSchema.optional(),
    title: z.string().min(1),
    body: z.string().min(1),
    show: z.array(CoachPresentationSchema).default([]),
    /** Canvas node to bring into view while the studio is closed. */
    focus: z
      .object({
        node: ContextKeySchema,
        /** Node type to fall back to while `node` is unbound. */
        orFirst: z.string().optional(),
        /** Leaves room on this side of the node for the card. */
        placement: SideSchema.optional(),
        /** Space around the node, as a share of the view (the canvas's fitView padding). */
        padding: z.number().min(0).max(2).optional(),
        /** The closest the camera comes (1 = the node at its own size). */
        maxZoom: z.number().positive().max(4).optional(),
      })
      .strict()
      .optional(),
    on: z.array(CoachTransitionSchema).default([]),
    /**
     * The step's one button. `placement: 'target'` puts it under what the card points at
     * (a "Skip for now" under a placeholder card) instead of on the card.
     */
    action: z
      .object({
        label: z.string().min(1),
        to: IdSchema,
        unbind: z.array(ContextKeySchema).default([]),
        placement: z.enum(['card', 'target']).default('card'),
      })
      .strict()
      .optional(),
    /**
     * Whether the step happens, by what the app knows when it begins: when this does not
     * hold, the run goes straight on to `otherwise` (a step only for people with a brand).
     */
    when: CoachConditionSchema.optional(),
    otherwise: IdSchema.optional(),
    /** Shown on the card above the copy; a presentation's own media replaces it. */
    media: CoachMediaSchema.optional(),
    /**
     * Nodes (by context key) the app keeps framed on screen while this step runs: both
     * brand groups as Poppy fills them, say. Framed again whenever something lands in one.
     */
    keepInView: z.array(ContextKeySchema).max(4).default([]),
    /**
     * The groups in `keepInView` are only framed, never filled: what lands loose on the board
     * stays loose (a first board, where a new script belongs beside the chat, not in a group).
     */
    framesOnly: z.boolean().optional(),
    /**
     * Groups (by context key) the app keeps laid out as a tidy stack while this step runs:
     * posts stacked in columns, profile cards fanned beside them (Inspiration Content).
     */
    stackGroups: z.array(ContextKeySchema).max(4).default([]),
    /**
     * Where a card that points at nothing sits: floating in a corner, or on the board itself,
     * centred over the canvas with the canvas dimmed around it (the side panel stays bright).
     */
    stage: z.enum(['float', 'board']).default('float'),
    /** Two to six buttons, each to its own step; `set` keeps what was chosen in context. */
    choices: z
      .array(
        z
          .object({
            label: z.string().min(1),
            to: IdSchema,
            /** Logos drawn before the label: the platforms or kind this choice stands for. */
            icons: z.array(ChoiceIconSchema).max(3).default([]),
            set: z.record(ContextKeySchema, z.string()).default({}),
          })
          .strict()
      )
      .min(2)
      .max(6)
      .optional(),
    /**
     * Moves on once every listed node has finished processing (or failed): answers being
     * transcribed before a chat reads them, say. Keys not set (a skipped answer) are not
     * waited for.
     */
    waitFor: z
      .object({
        nodes: z.array(ContextKeySchema).min(1),
        to: IdSchema,
        /** Moves on anyway after this many seconds: processing that never ends can't hold it. */
        atMost: z.number().int().positive().optional(),
      })
      .strict()
      .optional(),
    /** Work the app does for this step: change the board, or work the Copilot or a chat. */
    do: z
      .preprocess(
        value => (value === undefined || Array.isArray(value) ? value : [value]),
        z.array(CoachWorkSchema).min(1)
      )
      .optional(),
    form: CoachFormSchema.optional(),
    /** Where to restart if the page reloads during this step. */
    resumeAt: IdSchema.optional(),
  })
  .strict();

export const CoachJourneySchema = z
  .object({
    id: IdSchema,
    version: z.number().int().positive(),
    name: z.string().min(1),
    description: z.string().min(1),
    gate: z
      .object({ flag: z.enum(Object.keys(COACH_GATES) as [CoachGate, ...CoachGate[]]) })
      .strict()
      .optional(),
    /**
     * `guided` puts the board in tour mode while the journey runs on it: the header, the
     * toolbars and the canvas's own controls step back, the camera follows the steps, and the
     * header offers Restart and Exit. `standard` coaches over the board as it is.
     */
    mode: z.enum(['standard', 'guided']).default('standard'),
    /**
     * A backup for the Copilot's `[[coach:…]]` markers, which it sometimes leaves off: a reply
     * that says one of these words (any case) reports that signal too. Keep them to lines the
     * Copilot's instructions have it say only at that moment.
     */
    signalPhrases: z.record(z.string(), z.array(z.string().min(6)).min(1)).optional(),
    /** Whether cards carry a close button. A guided journey can be left from its header. */
    dismissible: z.boolean().default(true),
    /**
     * How the journey begins. Code can always start it (`coach.start`). `link`: also from
     * `/new-onboarding?coach=<id>`, on a fresh board. `auto: 'board'`: by itself on a board
     * the person can edit. `auto: 'invite'`: offered once, on a board the person can edit, with
     * its Tutorials welcome (Start or Not now); never after they finished or left it. `once`:
     * never by itself again after it was finished or dismissed.
     * `context`: keys whoever starts it passes in (`coach.start(id, { … })`), beyond `boardId`.
     */
    start: z
      .object({
        link: z.boolean().default(false),
        auto: z.enum(['board', 'invite']).optional(),
        once: z.boolean().default(true),
        context: z.array(ContextKeySchema).default([]),
      })
      .strict()
      .default({ link: false, once: true, context: [] }),
    /**
     * Lists the journey under Tutorials, to explore and replay. `on: 'this-board'` runs it on
     * the board the person is on; `'new-board'` on a fresh one (it must allow `start.link`).
     * The host app controls the Tutorials rollout; the journey's gate controls eligibility.
     */
    tutorial: z
      .object({
        description: z.string().min(1),
        on: z.enum(['this-board', 'new-board']),
      })
      .strict()
      .optional(),
    steps: z.array(CoachStepSchema).min(1),
  })
  .strict()
  .superRefine((journey, ctx) => {
    if (journey.tutorial?.on === 'new-board' && !journey.start.link)
      ctx.addIssue({
        code: 'custom',
        path: ['tutorial', 'on'],
        message: 'a tutorial on a new board starts by link: set `start.link`',
      });
    if (journey.start.auto === 'invite' && !journey.tutorial)
      ctx.addIssue({
        code: 'custom',
        path: ['start', 'auto'],
        message: 'an invite shows the Tutorials welcome: give the journey a `tutorial`',
      });
    const ids = new Set<string>();
    journey.steps.forEach((step, index) => {
      if (step.id === END_STEP)
        ctx.addIssue({
          code: 'custom',
          path: ['steps', index, 'id'],
          message: '"end" is reserved',
        });
      if (ids.has(step.id))
        ctx.addIssue({
          code: 'custom',
          path: ['steps', index, 'id'],
          message: 'duplicate step id',
        });
      ids.add(step.id);
    });
    const reachable = (target: string) => target === END_STEP || ids.has(target);

    journey.steps.forEach((step, index) => {
      const at = (...path: (string | number)[]) => ['steps', index, ...path];
      step.on.forEach((transition, t) => {
        if (!reachable(transition.to))
          ctx.addIssue({ code: 'custom', path: at('on', t, 'to'), message: 'unknown step' });
        const definition = COACH_EVENTS[transition.event];
        const payload: readonly string[] = definition.payload;
        const fields = [
          ...transition.match,
          ...Object.keys(transition.matchAs ?? {}),
          ...Object.keys(transition.bindAs ?? {}),
          ...transition.bind,
          ...Object.keys(transition.if ?? {}),
        ];
        for (const field of fields) {
          if (!payload.includes(field))
            ctx.addIssue({
              code: 'custom',
              path: at('on', t),
              message: `"${transition.event}" has no "${field}"`,
            });
        }
        if (transition.fresh && !('id' in definition))
          ctx.addIssue({
            code: 'custom',
            path: at('on', t, 'fresh'),
            message: `"${transition.event}" has no id to check freshness`,
          });
      });
      if ((step.display === 'inline') !== Boolean(step.slot))
        ctx.addIssue({
          code: 'custom',
          path: at('slot'),
          message: 'inline steps need a slot, and only inline steps take one',
        });
      if (step.action && !reachable(step.action.to))
        ctx.addIssue({ code: 'custom', path: at('action', 'to'), message: 'unknown step' });
      if (step.resumeAt && !ids.has(step.resumeAt))
        ctx.addIssue({ code: 'custom', path: at('resumeAt'), message: 'unknown step' });
      step.do?.forEach((work, w) => {
        checkWork(work, at('do', w), step.id, reachable, ctx);
        if (work.then && w < step.do!.length - 1)
          ctx.addIssue({
            code: 'custom',
            path: at('do', w, 'then'),
            message: 'only the last work in a list says where to go next',
          });
      });
      if (step.action?.placement === 'target' && !step.show.some(show => show.node))
        ctx.addIssue({
          code: 'custom',
          path: at('action', 'placement'),
          message: 'a button under the target needs the card to point at a node',
        });
      if (Boolean(step.when) !== Boolean(step.otherwise))
        ctx.addIssue({
          code: 'custom',
          path: at('otherwise'),
          message: 'a step with `when` needs `otherwise`, and only one with `when` takes it',
        });
      if (step.otherwise && !reachable(step.otherwise))
        ctx.addIssue({ code: 'custom', path: at('otherwise'), message: 'unknown step' });
      if (step.otherwise === step.id)
        ctx.addIssue({
          code: 'custom',
          path: at('otherwise'),
          message: 'a step cannot skip to itself',
        });
      if (step.choices) {
        if (step.display !== 'card' && step.display !== 'conversation')
          ctx.addIssue({ code: 'custom', path: at('choices'), message: 'only cards take choices' });
        if (step.action)
          ctx.addIssue({
            code: 'custom',
            path: at('choices'),
            message: 'a card has choices or one action button, not both',
          });
        step.choices.forEach((choice, c) => {
          if (!reachable(choice.to))
            ctx.addIssue({ code: 'custom', path: at('choices', c, 'to'), message: 'unknown step' });
        });
      }
      if (step.waitFor && !reachable(step.waitFor.to))
        ctx.addIssue({ code: 'custom', path: at('waitFor', 'to'), message: 'unknown step' });
      if (step.form) {
        if (step.display !== 'card' && step.display !== 'conversation')
          ctx.addIssue({
            code: 'custom',
            path: at('form'),
            message: 'only card steps take a form',
          });
        const answers = COACH_FORMS[step.form.id].answers;
        if (answers === 'node' && !step.form.accept)
          ctx.addIssue({
            code: 'custom',
            path: at('form', 'accept'),
            message: `"${step.form.id}" needs the node types it accepts`,
          });
        if (answers === 'node' && step.form.suggestions)
          ctx.addIssue({
            code: 'custom',
            path: at('form', 'suggestions'),
            message: 'only a typed answer takes suggestions',
          });
        if (answers === 'text' && (step.form.accept || !step.form.submit))
          ctx.addIssue({
            code: 'custom',
            path: at('form'),
            message: `"${step.form.id}" takes a \`submit\` label and no \`accept\``,
          });
        if (!step.do && !step.form.to)
          ctx.addIssue({
            code: 'custom',
            path: at('form', 'to'),
            message: 'a form needs `to`, or work (`do`) to run with the answer',
          });
        if (step.do && step.form.to)
          ctx.addIssue({
            code: 'custom',
            path: at('form', 'to'),
            message: "with `do`, where to go next is the last work's `then`",
          });
        if (!reachable(step.form.to ?? END_STEP))
          ctx.addIssue({ code: 'custom', path: at('form', 'to'), message: 'unknown step' });
      }
    });
    checkContextKeys(journey, ctx);
  });

/** Keys every run has from its start: the board it began on (or the one it waits for). */
const START_KEYS = ['boardId'];

/**
 * Every context key a step reads is one the journey sets somewhere (an event's payload, an
 * action's return, a form's answer, a choice) or is started with (`start.context`), so a misspelt key fails here instead of
 * leaving a card pointing at nothing.
 */
function checkContextKeys(
  journey: {
    start: { context: readonly string[] };
    steps: readonly z.output<typeof CoachStepSchema>[];
  },
  ctx: z.RefinementCtx
) {
  const written = new Set<string>([...START_KEYS, ...journey.start.context]);
  for (const step of journey.steps) {
    for (const transition of step.on) {
      transition.bind.forEach(key => written.add(key));
      Object.values(transition.bindAs ?? {}).forEach(key => written.add(key));
    }
    step.do?.forEach(work => Object.values(work.bind).forEach(key => written.add(key)));
    if (step.form) written.add(step.form.bind);
    step.choices?.forEach(choice => Object.keys(choice.set).forEach(key => written.add(key)));
  }
  const read = (key: string, path: (string | number)[]) => {
    if (!written.has(key))
      ctx.addIssue({ code: 'custom', path, message: `nothing in the journey sets "${key}"` });
  };
  journey.steps.forEach((step, index) => {
    const at = (...path: (string | number)[]) => ['steps', index, ...path];
    const slots = (text: string, path: (string | number)[]) => {
      for (const [, key] of text.matchAll(/\{\{([a-zA-Z0-9]+)\}\}/g)) read(key!, path);
    };
    slots(step.title, at('title'));
    slots(step.body, at('body'));
    step.keepInView.forEach((key, k) => read(key, at('keepInView', k)));
    step.stackGroups.forEach((key, k) => read(key, at('stackGroups', k)));
    step.show.forEach((show, s) => {
      if (show.node) read(show.node, at('show', s, 'node'));
      if (show.in) read(show.in, at('show', s, 'in'));
      if (show.title) slots(show.title, at('show', s, 'title'));
      if (show.body) slots(show.body, at('show', s, 'body'));
    });
    if (step.focus) read(step.focus.node, at('focus', 'node'));
    step.waitFor?.nodes.forEach((key, k) => read(key, at('waitFor', 'nodes', k)));
    step.on.forEach((transition, t) => {
      transition.match.forEach(key => read(key, at('on', t, 'match')));
      Object.values(transition.matchAs ?? {}).forEach(key => read(key, at('on', t, 'matchAs')));
    });
    step.do?.forEach((work, w) => {
      const params = (work.params ?? {}) as Record<string, unknown>;
      const refs: readonly string[] = COACH_ACTIONS[work.action].refs;
      for (const [name, value] of Object.entries(params)) {
        const path = at('do', w, 'params', name);
        if (refs.includes(name)) {
          for (const key of [value].flat()) if (typeof key === 'string') read(key, path);
        } else {
          for (const text of [value].flat())
            if (typeof text === 'string')
              for (const [, key] of text.matchAll(/\{\{([a-zA-Z0-9]+)\}\}/g)) read(key!, path);
        }
      }
    });
  });
}

/** A step's work: params the action accepts, binds of what it returns, real steps to go to. */
function checkWork(
  work: z.output<typeof CoachWorkSchema>,
  path: (string | number)[],
  stepId: string,
  reachable: (target: string) => boolean,
  ctx: z.RefinementCtx
) {
  const definition = COACH_ACTIONS[work.action];
  const params = definition.params.safeParse(work.params);
  if (!params.success) {
    for (const issue of params.error.issues)
      ctx.addIssue({
        code: 'custom',
        path: [...path, 'params', ...issue.path.map(String)],
        message: `"${work.action}": ${issue.message}`,
      });
  }
  const returns: readonly string[] = definition.returns;
  for (const key of Object.keys(work.bind)) {
    if (!returns.includes(key))
      ctx.addIssue({
        code: 'custom',
        path: [...path, 'bind', key],
        message: `"${work.action}" returns no "${key}"`,
      });
  }
  for (const key of ['then', 'onError'] as const) {
    const target = work[key];
    if (target && !reachable(target))
      ctx.addIssue({ code: 'custom', path: [...path, key], message: 'unknown step' });
    // Going back to the same step would begin its work again, forever if it keeps failing.
    if (target === stepId)
      ctx.addIssue({
        code: 'custom',
        path: [...path, key],
        message: 'work cannot lead back to its own step',
      });
  }
}

export type CoachJourneyInput = z.input<typeof CoachJourneySchema>;
export type CoachTransitionInput = z.input<typeof CoachTransitionSchema>;
export type CoachJourney = z.output<typeof CoachJourneySchema>;
export type CoachStep = CoachJourney['steps'][number];
export type CoachWork = NonNullable<CoachStep['do']>[number];
export type CoachForm = NonNullable<CoachStep['form']>;
export type CoachMedia = z.output<typeof CoachMediaSchema>;
export type CoachChoice = NonNullable<CoachStep['choices']>[number];
export type CoachPresentation = z.output<typeof CoachPresentationSchema>;
export type CoachCondition = z.output<typeof CoachConditionSchema>;
export type CoachSide = z.output<typeof SideSchema>;
export type CoachContextValue = z.infer<typeof ContextValueSchema>;

/** Parses a journey, listing every problem when it is invalid. */
export function parseJourney(
  input: CoachJourneyInput
): { ok: true; journey: CoachJourney } | { ok: false; error: string } {
  const parsed = CoachJourneySchema.safeParse(input);
  if (parsed.success) return { ok: true, journey: parsed.data };
  const problems = parsed.error.issues
    .map(issue => `  ${issue.path.join('.')}: ${issue.message}`)
    .join('\n');
  return { ok: false, error: `Invalid coach journey "${input.id}":\n${problems}` };
}

/** Parses a journey and throws with every problem listed if it is invalid (tests and tools). */
export function defineJourney(input: CoachJourneyInput): CoachJourney {
  const parsed = parseJourney(input);
  if (!parsed.ok) throw new Error(parsed.error);
  return parsed.journey;
}
